import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useForm } from "react-hook-form";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";

interface CompleteResetFormValues {
  email: string;
  temporaryPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface Props {
  defaultEmail: string;
  resetToken: string;
  inputClassName: string;
  onSuccess: () => Promise<void>;
  onClose: () => void;
}

export default ({ defaultEmail, resetToken, inputClassName, onSuccess, onClose }: Props) => {
  const completeResetForm = useForm<CompleteResetFormValues>({
    defaultValues: {
      email: defaultEmail,
      temporaryPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = completeResetForm.handleSubmit(async values => {
    setError(null);

    if (values.newPassword !== values.confirmPassword) {
      setError("New password and confirmation must match.");
      return;
    }

    const response = await fetch("/api/auth/password/reset/complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: values.email,
        token: resetToken,
        temporaryPassword: values.temporaryPassword,
        newPassword: values.newPassword,
      }),
    });
    const result = await response.json().catch(() => null) as { error?: string } | null;

    if (!response.ok) {
      setError(result?.error ?? "Unable to update password.");
      return;
    }

    await onSuccess();
  });

  return (
    <Dialog title="Set a new password" subtitle="Use the temporary password from your email, then choose a new one." onClose={onClose} TitleIcon={KeyRound}>
      <form className="space-y-4" onSubmit={event => void handleSubmit(event)}>
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700" htmlFor="complete-reset-email">Email</label>
          <input
            id="complete-reset-email"
            type="email"
            autoComplete="email"
            className={inputClassName}
            {...completeResetForm.register("email", { required: true })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700" htmlFor="temporary-password">Temporary password</label>
          <input
            id="temporary-password"
            type="password"
            autoComplete="one-time-code"
            className={inputClassName}
            {...completeResetForm.register("temporaryPassword", { required: true })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700" htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            className={inputClassName}
            {...completeResetForm.register("newPassword", { required: true })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700" htmlFor="confirm-password">Confirm new password</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            className={inputClassName}
            {...completeResetForm.register("confirmPassword", { required: true })}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={completeResetForm.formState.isSubmitting}>
            {completeResetForm.formState.isSubmitting ? "Updating..." : "Update password"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
